import { Point, Rect } from './Shapes';
const map = (arr, func) => arr.map(func);
/**
 * SVG transformation represented as a vector.
 */
export class MatrixVector {
    constructor(a, b, c, d, e, f) {
        this.a = a || 0;
        this.b = b || 0;
        this.c = c || 0;
        this.d = d || 0;
        this.e = e || 0;
        this.f = f || 0;
    }
    fromMatrix(m) {
        const v = new MatrixVector();
        v.a = m.a;
        v.b = m.b;
        v.c = m.c;
        v.d = m.d;
        v.e = m.e;
        v.f = m.f;
        return v;
    }
}
/**
 * SVG transformation matrix.
 */
export class Matrix {
    constructor(a, b, c, d, e, f) {
        this.a = a || 0;
        this.b = b || 0;
        this.c = c || 0;
        this.d = d || 0;
        this.e = e || 0;
        this.f = f || 0;
    }
    plus(m) {
        this.a += m.a;
        this.b += m.b;
        this.c += m.c;
        this.d += m.d;
        this.e += m.e;
        this.f += m.f;
    }
    minus(m) {
        this.a -= m.a;
        this.b -= m.b;
        this.c -= m.c;
        this.d -= m.d;
        this.e -= m.e;
        this.f -= m.f;
    }
    times(m) {
        return new Matrix(this.a * m.a + this.c * m.b, this.b * m.a + this.d * m.b, this.a * m.c + this.c * m.d, this.b * m.c + this.d * m.d, this.a * m.e + this.c * m.f + this.e, this.b * m.e + this.d * m.f + this.f);
    }
    apply(p) {
        return new Point(this.a * p.x + this.c * p.y + this.e, this.b * p.x + this.d * p.y + this.f);
    }
    applyRect(r) {
        return Rect.fromPoints(this.apply(r.topLeft()), this.apply(r.bottomRight()));
    }
    toString() {
        return 'matrix(' + this.a + ' ' + this.b + ' ' + this.c + ' ' + this.d + ' ' + this.e + ' ' + this.f + ')';
    }
    static fromSVGMatrix(vm) {
        const m = new Matrix();
        m.a = vm.a;
        m.b = vm.b;
        m.c = vm.c;
        m.d = vm.d;
        m.e = vm.e;
        m.f = vm.f;
        return m;
    }
    static fromMatrixVector(v) {
        const m = new Matrix();
        m.a = v.a;
        m.b = v.b;
        m.c = v.c;
        m.d = v.d;
        m.e = v.e;
        m.f = v.f;
        return m;
    }
    static fromList(v) {
        if (v.length !== 6) {
            throw new Error('The given list should consist of six elements.');
        }
        const m = new Matrix();
        m.a = v[0];
        m.b = v[1];
        m.c = v[2];
        m.d = v[3];
        m.e = v[4];
        m.f = v[5];
        return m;
    }
    static translation(x, y) {
        const m = new Matrix();
        m.a = 1;
        m.b = 0;
        m.c = 0;
        m.d = 1;
        m.e = x;
        m.f = y;
        return m;
    }
    static unit() {
        return new Matrix(1, 0, 0, 1, 0, 0);
    }
    static rotation(angle, x, y) {
        const m = new Matrix();
        m.a = Math.cos(angle * Math.PI / 180);
        m.b = Math.sin(angle * Math.PI / 180);
        m.c = -m.b;
        m.d = m.a;
        m.e = (x - x * m.a + y * m.b) || 0;
        m.f = (y - y * m.a - x * m.b) || 0;
        return m;
    }
    static scaling(scaleX, scaleY) {
        const m = new Matrix();
        m.a = scaleX;
        m.b = 0;
        m.c = 0;
        m.d = scaleY;
        m.e = 0;
        m.f = 0;
        return m;
    }
    static parse(v) {
        let parts, nums;
        if (v) {
            v = v.trim();
            // of the form "matrix(...)"
            if (v.slice(0, 6).toLowerCase() === 'matrix') {
                nums = v.slice(7, v.length - 1).trim();
                parts = nums.split(',');
                if (parts.length === 6) {
                    return Matrix.fromList(map(parts, function (p) {
                        return parseFloat(p);
                    }));
                }
                parts = nums.split(' ');
                if (parts.length === 6) {
                    return Matrix.fromList(map(parts, function (p) {
                        return parseFloat(p);
                    }));
                }
            }
            // of the form "(...)"
            if (v.slice(0, 1) === '(' && v.slice(v.length - 1) === ')') {
                v = v.substr(1, v.length - 1);
            }
            if (v.indexOf(',') > 0) {
                parts = v.split(',');
                if (parts.length === 6) {
                    return Matrix.fromList(map(parts, function (p) {
                        return parseFloat(p);
                    }));
                }
            }
            if (v.indexOf(' ') > 0) {
                parts = v.split(' ');
                if (parts.length === 6) {
                    return Matrix.fromList(map(parts, function (p) {
                        return parseFloat(p);
                    }));
                }
            }
        }
        return parts;
    }
}
